import clx from 'classnames';
import { InputHTMLAttributes } from 'react';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type VisualRadioProps = InputHTMLAttributes<HTMLInputElement> & {
  value: string;
  icon?: string;
  image?: string;
  id: string;
  supportingText?: string;
  label: string;
  isFullWidth?: boolean;
  labelClassName?: string;
  field: ControllerRenderProps<FieldValues, string>;
};

const VisualRadio = ({
  disabled,
  label,
  id,
  checked,
  icon,
  supportingText,
  value,
  image,
  isFullWidth,
  labelClassName,
  onChange,
  name,
  field,
}: VisualRadioProps) => {
  const { t } = useTranslation();

  const computedDisabled = checked ? false : disabled;

  return (
    <label
      className={clx(
        'relative flex flex-col items-center p-4 rounded-lg w-full h-full',
        ' hover:bg-neutral-100 border-4',
        'transition-colors duration-quick ease-out',
        !isFullWidth && 'max-w-56',
        checked ? 'border-primary-500  bg-neutral-50' : 'bg-neutral-100 border-transparent',
        computedDisabled && 'bg-neutral-50 text-system-disabled-400',
        icon ? 'pb-2 gap-4' : 'justify-center',
      )}
      htmlFor={id}
    >
      {icon && (
        <i
          className={clx(
            icon,
            'text-5xl transition-all',
            checked && 'text-primary-500',
            computedDisabled && 'text-system-disabled-300',
            'text-neutral-500',
          )}
        />
      )}
      <input
        {...field}
        checked={checked}
        className="appearance-none"
        disabled={computedDisabled}
        id={id}
        name={name}
        onChange={onChange}
        type="radio"
        value={value}
      />
      {image && <img alt="" src={image} />}
      <div>
        <div className="flex items-center gap-2 relative">
          <p className={clx('uppercase w-full text-center md-bold-caps', labelClassName)}>{t(label)}</p>
        </div>
        {supportingText && (
          <p className={clx('md-regular text-neutral-400', disabled && 'text-system-disabled-400')}>
            {t(supportingText)}
          </p>
        )}
      </div>
    </label>
  );
};

export default VisualRadio;
